/**
 * /imports — universal CSV importer admin.
 *
 * One screen serves every importer kind. Operator picks a kind, drops a
 * CSV (or pastes), fills in any required context fields, clicks Preview.
 * Preview shows a per-row table (color-coded ok / warning / duplicate /
 * error). When the preview looks right, click Commit to apply.
 *
 * Adding a new importer to this UI requires zero changes — kinds + their
 * context_keys come from GET /api/imports/_kinds, and any per-kind UI
 * affordance (e.g., bank_account picker for bank_transactions) is
 * registered in CONTEXT_RENDERERS below.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";

import {
  bankAccountsListQueryOptions,
} from "@/lib/queries";
import {
  importKindsQueryOptions,
  useCommitImport,
  usePreviewImport,
  useUploadPreviewImport,
  useUploadCommitImport,
  useDetectKind,
  useInspectImport,
  type ImportPreview,
  type ImportResult,
  type InspectResponse,
  type RowOutcome,
  type RowStatus,
} from "@/lib/imports";
import { useToast } from "@/components/Toaster";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/imports")({
  component: ImportsPage,
});

// ──────────────────────────────────────────────────────────────
// Kinds that bypass the standard preview/commit flow and go
// directly to /api/chat/upload-csv (format auto-detected there).
// These are not registered in the server's /_kinds endpoint.
// ──────────────────────────────────────────────────────────────
const DIRECT_IMPORT_KINDS = ["carta_cap_table", "stripe_billing_csv"] as const;
type DirectImportResult = {
  format: string;
  imported: boolean;
  summary: string;
  detail: string | null;
  link: string | null;
  errors: string[];
};


// ──────────────────────────────────────────────────────────────
// Friendly labels per kind. Anything not in here falls back to the
// raw kind name from /_kinds (so adding a new importer doesn't break
// the page even before this map is updated).
// ──────────────────────────────────────────────────────────────
const KIND_META: Record<string, { label: string; description: string; example: string }> = {
  bank_transactions: {
    label: "Bank transactions",
    description:
      "Drop a CSV from your bank (Chase, BofA, Wells, etc.). Each row " +
      "becomes a posted journal — deposit (positive amount) or withdrawal " +
      "(negative). Rows without a gl_code route to Suspense (6900) for " +
      "later recategorization.",
    example:
      "date,amount,description\n" +
      "2026-04-15,1500.00,Customer payment - ABC Co\n" +
      "2026-04-15,-87.50,Office supplies\n" +
      "2026-04-16,-1250.00,Sub payment - Concrete LLC\n",
  },
  ap_invoices: {
    label: "AP aging / open bills (accounts payable)",
    description:
      "Bring your unpaid vendor bills over from another system — this becomes " +
      "your accounts payable (a liability). Just drop your AP aging detail: the " +
      "columns auto-map (vendor / supplier / amount / due date). Turn on " +
      "'auto_create_parties' and any vendor that doesn't exist yet is created " +
      "for you, so the whole aging imports in one step. Set 'overdue_threshold_days' " +
      "(e.g. 150) to see how much is badly overdue. Always Preview first — it shows " +
      "exactly what will happen and writes nothing.",
    example:
      "vendor_code,vendor_name,invoice_no,invoice_date,due_date,amount,gl_code\n" +
      "ACME,Acme Supply Co,INV-001,2026-01-15,2026-02-14,5000.00,5100\n" +
      "ACME,Acme Supply Co,INV-002,2026-01-16,2026-02-15,1200.00,5200\n",
  },
  ar_invoices: {
    label: "AR aging / open invoices (accounts receivable)",
    description:
      "Bring your unpaid customer invoices over — this becomes your accounts " +
      "receivable (an asset). Drop your AR aging detail; columns auto-map " +
      "(customer / client / amount / due date). Turn on 'auto_create_parties' to " +
      "create any missing customers automatically, and set 'overdue_threshold_days' " +
      "(e.g. 150) to flag what's badly overdue. Always Preview first.",
    example:
      "customer_code,customer_name,invoice_no,invoice_date,due_date,amount,gl_code\n" +
      "BIGCO,Big Co Ltd,AR-100,2026-01-15,2026-02-14,12500.00,4100\n",
  },
  chart_of_accounts: {
    label: "Chart of accounts",
    description:
      "Bulk-create GL accounts. The first thing to import when setting up " +
      "a new entity. Parent codes may reference accounts earlier in the " +
      "same file. Account types: asset, liability, equity, revenue, expense.",
    example:
      "code,name,account_type,parent_code,is_postable\n" +
      "1000,Assets,asset,,false\n" +
      "1100,Cash,asset,1000,true\n" +
      "1200,Accounts Receivable,asset,1000,true\n" +
      "2000,Liabilities,liability,,false\n" +
      "2100,Accounts Payable,liability,2000,true\n" +
      "3000,Equity,equity,,false\n" +
      "4000,Revenue,revenue,,true\n" +
      "5000,Expenses,expense,,true\n",
  },
  customers: {
    label: "Customers",
    description:
      "Bulk-import AR parties (customers). Required: code + name. " +
      "Common optional columns: email, phone, terms (net days), tax_id, " +
      "credit_limit, address.",
    example:
      "code,name,email,terms,credit_limit\n" +
      "BIGCO,Big Co Construction,ap@bigco.com,30,250000\n" +
      "ACME,Acme Industries,billing@acme.com,45,100000\n",
  },
  vendors: {
    label: "Vendors",
    description:
      "Bulk-import AP parties (vendors / subcontractors / suppliers). " +
      "Required: code + name. Optional: tax_id (for 1099), is_1099, " +
      "license_no, insurance_expires, workers_comp_expires, prequalified, " +
      "payment terms, address.",
    example:
      "code,name,tax_id,is_1099,terms,license_no\n" +
      "CONCRETELLC,Concrete LLC,12-3456789,true,30,CA-987654\n" +
      "OFFICE_DEPOT,Office Depot,,false,30,\n",
  },
  projects: {
    label: "Projects (jobs)",
    description:
      "Bulk-import construction projects. Required: code + name. " +
      "Customer_code links to a customer that must already exist. " +
      "Status: bidding/awarded/active/hold/closed. Billing methods: " +
      "lump_sum, aia_progress, time_and_materials, cost_plus, unit_price.",
    example:
      "code,name,customer_code,status,billing_method,original_contract_amount,start_date\n" +
      "P-2026-001,Highland Tower,BIGCO,active,aia_progress,11500000,2026-01-15\n" +
      "P-2026-002,Lobby Renovation,ACME,bidding,lump_sum,450000,\n",
  },
  opening_trial_balance: {
    label: "Opening trial balance",
    description:
      "Cutover: import opening balances from the prior system as one " +
      "balanced journal. Required context: as_of_date (use the close " +
      "date of the prior system). Total debits must equal total credits. " +
      "Import the chart of accounts FIRST — every gl_code must exist.",
    example:
      "gl_code,debit,credit,description\n" +
      "1100,150000.00,0,Cash on hand at cutover\n" +
      "1200,85000.00,0,Open AR at cutover\n" +
      "2100,0,42000.00,Open AP at cutover\n" +
      "3000,0,193000.00,Retained earnings (plug to balance)\n",
  },
  payroll_employees: {
    label: "Payroll employees",
    description:
      "Bulk-import W-2 employee master records from ADP / Gusto / Paychex / " +
      "QuickBooks Payroll via generic CSV. Includes W-4 setup and direct " +
      "deposit. Required: employee_number, name, hire_date, pay_type, " +
      "default_rate, pay_frequency, work_state.",
    example:
      "employee_number,first_name,last_name,ssn,hire_date,pay_type,default_rate,pay_frequency,work_state,fed_filing_status,fed_dependents_amt,bank_routing,bank_account,bank_account_type\n" +
      "EMP001,Jane,Smith,123456789,2024-03-15,salary,85000,biweekly,CA,married,4000,121000248,1234567890,checking\n" +
      "EMP002,John,Doe,987654321,2025-01-10,hourly,42.50,biweekly,CA,single,0,121000248,9876543210,checking\n",
  },
  payroll_employee_ytd: {
    label: "Payroll YTD balances (mid-year cutover)",
    description:
      "Mid-year cutover: per-employee YTD wages and taxes from the prior " +
      "payroll system. Creates ONE synthetic 'YTD-OPEN' pay run so W-2s and " +
      "941s at year-end correctly reflect full-year totals. Required context: " +
      "as_of_date. IMPORT EMPLOYEES FIRST.",
    example:
      "employee_number,ytd_gross_wages,ytd_federal_income_tax,ytd_social_security,ytd_medicare,ytd_state_income_tax,ytd_net_pay,ytd_401k,ytd_health,ytd_employer_ss,ytd_employer_medicare,ytd_employer_futa,ytd_employer_sui\n" +
      "EMP001,42500.00,4250.00,2635.00,616.25,1700.00,30298.75,2125.00,975.00,2635.00,616.25,42.00,510.00\n" +
      "EMP002,18750.00,1200.00,1162.50,271.88,562.50,14803.12,750.00,0,1162.50,271.88,42.00,225.00\n",
  },
  // ── Direct-import kinds (bypass preview/commit) ───────────────
  carta_cap_table: {
    label: "Cap table (Carta export)",
    description:
      "Drop a Carta stakeholders CSV (Cap Table → Stakeholders → Export → CSV). " +
      "Bynaus auto-detects the Carta format and imports shareholders, instrument " +
      "classes (Common, Preferred, SAFE, Convertible Note, Options), and " +
      "transactions. Shareholders and instruments are get-or-created — safe to " +
      "re-import with new rows. Typical file name: stakeholders_YYYY-MM-DD.csv.",
    example:
      "Stakeholder Name,Security Type,Issued Shares,Issue Price,Total,Issue Date\n" +
      "Alice Chen,Series A Preferred,100000,1.50,150000,2024-03-15\n" +
      "Bob Smith,Common Stock,50000,0.10,5000,2023-01-01\n" +
      "Sequoia,SAFE,,,500000,2023-06-30\n",
  },
  stripe_billing_csv: {
    label: "Billing ledger (Stripe CSV)",
    description:
      "Drop a Stripe billing export (Dashboard → Revenue → MRR → Export CSV, or " +
      "Dashboard → Billing → Subscriptions → Export CSV). Bynaus creates customer " +
      "records, plans, and subscriptions. ARR, MRR, NRR, and cohort retention " +
      "compute immediately from real data. Subscriptions are deduped on Stripe " +
      "subscription ID — re-importing is safe. For live sync, use " +
      "'Import my Stripe subscriptions with key sk_live_…' in the CFO Advisor chat.",
    example:
      "Customer,Plan,MRR,Status,Created\n" +
      "Acme Corp,Growth,499,active,2024-01-15\n" +
      "Widget Co,Starter,99,active,2024-02-01\n" +
      "Old Corp,Growth,499,canceled,2023-12-01\n",
  },
};

// Preview summary keys meant for the chat assistant (plan hash, how the file's
// columns and first rows read) — the wizard's banner leaves them out.
const CHAT_ONLY_SUMMARY_KEYS = new Set([
  "plan_sha256", "file_columns", "import_fields", "first_rows", "skipped_rows",
]);

const STATUS_COLOR: Record<RowStatus, string> = {
  ok:        "#16a34a",  // green
  warning:   "#d97706",  // amber
  duplicate: "#64748b",  // slate
  error:     "#dc2626",  // red
};
const STATUS_BG: Record<RowStatus, string> = {
  ok:        "#dcfce7",
  warning:   "#fef3c7",
  duplicate: "#f1f5f9",
  error:     "#fee2e2",
};


// Local kinds that don't come from the server (direct-import path).
const LOCAL_EXTRA_KINDS = DIRECT_IMPORT_KINDS.map((name) => ({
  name,
  context_keys: {} as Record<string, string>,
}));

function ImportsPage() {
  const toast = useToast();
  const kindsQuery = useQuery(importKindsQueryOptions());
  const serverKinds = kindsQuery.data ?? [];
  // Merge server kinds with the local direct-import kinds (append at end).
  const kinds = [...serverKinds, ...LOCAL_EXTRA_KINDS];

  const [selectedKind, setSelectedKind] = useState<string>("");
  const [csvText, setCsvText] = useState<string>("");
  const [xlsxFile, setXlsxFile] = useState<File | null>(null);
  const [context, setContext] = useState<Record<string, unknown>>({});
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [detectMsg, setDetectMsg] = useState<string | null>(null);
  const [directResult, setDirectResult] = useState<DirectImportResult | null>(null);
  const [directPending, setDirectPending] = useState(false);
  // Column mapping: source-header → target-field, plus the inspect result that
  // drives the mapping UI (the importer's fields + the file's headers).
  const [inspect, setInspect] = useState<InspectResponse | null>(null);
  const [columnMap, setColumnMap] = useState<Record<string, string>>({});
  const [mappingOpen, setMappingOpen] = useState(false);

  const isDirectImport = (DIRECT_IMPORT_KINDS as readonly string[]).includes(selectedKind);

  const previewMut = usePreviewImport();
  const commitMut = useCommitImport();
  const uploadPreviewMut = useUploadPreviewImport();
  const uploadCommitMut = useUploadCommitImport();
  const detectMut = useDetectKind();
  const inspectMut = useInspectImport();

  const meta = KIND_META[selectedKind];
  const kindInfo = kinds.find((k) => k.name === selectedKind);

  const onSelectKind = (kind: string) => {
    setSelectedKind(kind);
    setCsvText("");
    setXlsxFile(null);
    setContext({});
    setPreview(null);
    setResult(null);
    setDetectMsg(null);
    setDirectResult(null);
    setInspect(null);
    setColumnMap({});
  };

  // Build a File from whichever input the user gave (xlsx upload or pasted CSV).
  const currentFile = (): File | null =>
    xlsxFile ?? (csvText.trim() ? new File([csvText], "paste.csv", { type: "text/csv" }) : null);

  // Fetch the importer's target fields + the file's headers + a suggested map,
  // and pre-fill the mapping with the suggestion. Returns the response so
  // preview/commit can use it in the same tick (state updates are async).
  const runInspect = async (): Promise<InspectResponse | null> => {
    const file = currentFile();
    if (!file || !selectedKind) return null;
    try {
      const insp = await inspectMut.mutateAsync({ kind: selectedKind, file });
      setInspect(insp);
      setColumnMap(insp.suggested_map || {});
      return insp;
    } catch (err) {
      toast.error(`Could not read columns: ${(err as Error).message}`);
      return null;
    }
  };

  const runDirectImport = async () => {
    const file = xlsxFile ?? (csvText.trim() ? new File([csvText], "import.csv", { type: "text/csv" }) : null);
    if (!file) return;
    setDirectPending(true);
    setDirectResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api<DirectImportResult>("/api/chat/upload-csv", { method: "POST", body: fd });
      setDirectResult(r);
      if (r.imported) {
        toast.success(r.summary);
      } else {
        toast.error(r.summary || "Import failed — check errors below");
      }
    } catch (err) {
      toast.error(`Import failed: ${(err as Error).message}`);
    } finally {
      setDirectPending(false);
    }
  };

  /** Universal file-drop: CSV → read as text; XLSX → store File for upload. */
  const handleFile = async (f: File) => {
    setPreview(null);
    setResult(null);
    setDetectMsg(null);
    // A new file invalidates the previous inspect (headers, suggested map,
    // and any structure-healed rows all belong to the old file).
    setInspect(null);
    setColumnMap({});
    const lower = f.name.toLowerCase();
    const isXlsx = lower.endsWith(".xlsx") || lower.endsWith(".xlsm");

    if (isXlsx) {
      setXlsxFile(f);
      setCsvText("");
    } else {
      setXlsxFile(null);
      setCsvText(await f.text());
    }

    // Auto-detect kind unless the user already picked one
    if (!selectedKind) {
      try {
        const det = await detectMut.mutateAsync(f);
        if (det.detected_kind) {
          setSelectedKind(det.detected_kind);
          const pctLabel = `${Math.round(det.confidence * 100)}%`;
          setDetectMsg(
            `Auto-detected: ${KIND_META[det.detected_kind]?.label ?? det.detected_kind} ` +
            `(confidence ${pctLabel}). Switch above if wrong.`,
          );
        } else {
          setDetectMsg(
            "Could not auto-detect — pick the importer kind above.",
          );
        }
      } catch {
        // Detect is best-effort; ignore failures
      }
    }
  };

  // The mapping dialog stores {target_field: source_header} (one row per
  // importer field); the import API applies {source_header: target_field}.
  // Invert before sending — passing the dialog's orientation straight through
  // silently ignores every manual remap.
  const apiColumnMap = (
    targetToSource: Record<string, string>,
  ): Record<string, string> | undefined => {
    const out: Record<string, string> = {};
    for (const [target, source] of Object.entries(targetToSource)) {
      if (source) out[source] = target;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };

  // Inspect once (running the structure self-heal at most one time), then
  // preview/commit from the same healed rows. Re-uploading the original file
  // after a heal would re-run the LLM per step — slow, costly, and the two
  // passes could disagree.
  const ensureInspect = async (): Promise<InspectResponse | null> =>
    inspect ?? (await runInspect());

  // When we preview/commit already-healed structured_rows, prepare_import_rows
  // sees ready rows and returns structure_healed:false. Stamp the inspect
  // heal meta back onto the summary so the UI still shows what happened.
  const stampInspectHeal = <T extends { summary?: Record<string, unknown> }>(
    r: T,
    insp: InspectResponse | null | undefined,
  ): T => {
    if (!insp?.structure_healed) return r;
    const summary: Record<string, unknown> = { ...(r.summary || {}) };
    summary.structure_healed = true;
    if (insp.mapped_by) summary.mapped_by = insp.mapped_by;
    if (insp.reply) summary.structure_reply = insp.reply;
    const notes = (insp.data_notes || []).map(String);
    if (notes.length) {
      const existing = Array.isArray(summary.data_notes)
        ? (summary.data_notes as unknown[]).map(String)
        : [];
      summary.data_notes = [
        ...existing,
        ...notes.filter((n) => !existing.includes(n)),
      ];
    }
    return { ...r, summary };
  };

  const runPreview = async () => {
    if (!selectedKind) return;
    setResult(null);
    try {
      const insp = await ensureInspect();
      // If we just auto-inspected, columnMap state hasn't flushed yet — use
      // the fresh suggestion; otherwise columnMap carries the user's edits.
      const mapToSend = apiColumnMap(inspect ? columnMap : (insp?.suggested_map ?? columnMap));
      let r: ImportPreview;
      if (insp?.structure_healed && insp.structured_rows?.length) {
        r = await previewMut.mutateAsync({
          kind: selectedKind, rows: insp.structured_rows, context,
        });
        r = stampInspectHeal(r, insp);
      } else if (xlsxFile) {
        const wrap = await uploadPreviewMut.mutateAsync({
          kind: selectedKind, file: xlsxFile, context, column_map: mapToSend,
        });
        if (!wrap.preview) throw new Error("server returned no preview");
        r = wrap.preview;
      } else {
        r = await previewMut.mutateAsync({
          kind: selectedKind, csv_text: csvText, context, column_map: mapToSend,
        });
      }
      setPreview(r);
    } catch (err) {
      toast.error(`Preview failed: ${(err as Error).message}`);
    }
  };

  const runCommit = async () => {
    if (!preview || !selectedKind) return;
    try {
      const mapToSend = apiColumnMap(columnMap);
      let r: ImportResult;
      if (inspect?.structure_healed && inspect.structured_rows?.length) {
        r = await commitMut.mutateAsync({
          kind: selectedKind, rows: inspect.structured_rows, context,
        });
        r = stampInspectHeal(r, inspect);
      } else if (xlsxFile) {
        const wrap = await uploadCommitMut.mutateAsync({
          kind: selectedKind, file: xlsxFile, context, column_map: mapToSend,
        });
        if (!wrap.result) throw new Error("server returned no result");
        r = wrap.result;
      } else {
        r = await commitMut.mutateAsync({
          kind: selectedKind, csv_text: csvText, context, column_map: mapToSend,
        });
      }
      setResult(r);
      toast.success(
        `Imported ${r.created_count} of ${r.total_rows}` +
        (r.duplicate_count ? `, skipped ${r.duplicate_count} duplicate(s)` : "") +
        (r.error_count ? `, ${r.error_count} error(s)` : ""),
      );
    } catch (err) {
      toast.error(`Commit failed: ${(err as Error).message}`);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Imports</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            CSV bulk import for cutover and ongoing operations
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4 space-y-4">
        {/* Import a past job → archived history that seeds cost history */}
        <ImportPastJobCard />

        {/* Kind selector */}
        <div className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
          <div className="text-sm font-medium text-slate-800 mb-2 dark:text-slate-200">
            1. What are you importing?
          </div>
          <div className="flex flex-wrap gap-2">
            {kindsQuery.isLoading && (
              <span className="text-sm text-slate-500 dark:text-slate-400">Loading kinds…</span>
            )}
            {kinds.map((k) => {
              const m = KIND_META[k.name];
              const active = k.name === selectedKind;
              return (
                <button
                  key={k.name}
                  onClick={() => onSelectKind(k.name)}
                  className={
                    "px-3 py-2 text-sm rounded-md border transition " +
                    (active
                      ? "bg-amber-50 border-amber-500 text-amber-800 dark:bg-amber-900/30 dark:border-amber-500 dark:text-amber-300"
                      : "bg-white text-slate-800 border-slate-300 hover:border-slate-400 dark:bg-gray-900 dark:text-slate-200 dark:border-gray-700 dark:hover:border-gray-600")
                  }
                >
                  {m?.label ?? k.name}
                </button>
              );
            })}
          </div>
          {meta && (
            <p className="text-xs text-slate-600 mt-3 dark:text-slate-300">{meta.description}</p>
          )}
        </div>

        {/* Context (per-kind) + CSV input */}
        {selectedKind && (
          <>
            {kindInfo && Object.keys(kindInfo.context_keys).length > 0 && (
              <div className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
                <div className="text-sm font-medium text-slate-800 mb-2 dark:text-slate-200">
                  2. Settings for this import
                </div>
                <ContextEditor
                  kind={selectedKind}
                  contextKeys={kindInfo.context_keys}
                  context={context}
                  onChange={setContext}
                />
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
              <div className="flex items-center mb-2">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
                  3. Drop a file or paste rows
                </div>
                <div className="flex-1" />
                {meta && (
                  <Button
                    size="small"
                    onClick={() => {
                      setXlsxFile(null);
                      setCsvText(meta.example);
                    }}
                  >
                    Use example
                  </Button>
                )}
              </div>
              <div
                className={
                  "border-2 border-dashed rounded-md p-3 text-center text-xs mb-2 transition " +
                  (xlsxFile
                    ? "border-emerald-400 bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-300"
                    : "border-slate-300 bg-slate-50 text-slate-500 dark:bg-gray-950 dark:border-gray-700 dark:text-slate-400")
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={async (e) => {
                  e.preventDefault();
                  const f = e.dataTransfer.files?.[0];
                  if (f) await handleFile(f);
                }}
              >
                {xlsxFile ? (
                  <span>
                    📊 <strong>{xlsxFile.name}</strong> ({Math.round(xlsxFile.size / 1024)} KB)
                    {" — "}
                    <button
                      className="underline hover:text-emerald-900"
                      onClick={() => { setXlsxFile(null); setPreview(null); setResult(null); }}
                    >
                      remove
                    </button>
                  </span>
                ) : (
                  <span>
                    Drop a <strong>.csv</strong> or <strong>.xlsx</strong> file here,
                    or paste CSV below.{" "}
                    <label className="text-indigo-600 underline cursor-pointer hover:text-indigo-800">
                      browse
                      <input
                        type="file"
                        accept=".csv,.tsv,.txt,.xlsx,.xlsm"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (f) await handleFile(f);
                        }}
                      />
                    </label>
                  </span>
                )}
              </div>
              {detectMsg && (
                <div className="text-xs text-indigo-600 dark:text-indigo-400 mb-2">
                  {detectMsg}
                </div>
              )}
              {!xlsxFile && (
                <TextField
                  multiline
                  minRows={6}
                  maxRows={16}
                  fullWidth
                  size="small"
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                  placeholder="header_one,header_two,...&#10;value,value,..."
                  inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
                />
              )}
              <div className="mt-3 flex items-center gap-2">
                {isDirectImport ? (
                  <Button
                    variant="contained"
                    color="success"
                    disabled={(!csvText.trim() && !xlsxFile) || directPending}
                    onClick={runDirectImport}
                    startIcon={directPending ? <CircularProgress size={14} /> : null}
                  >
                    {directPending ? "Importing…" : "Import"}
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outlined"
                      disabled={(!csvText.trim() && !xlsxFile) || !selectedKind || inspectMut.isPending}
                      onClick={async () => { await runInspect(); setMappingOpen(true); }}
                      startIcon={inspectMut.isPending ? <CircularProgress size={14} /> : null}
                    >
                      {inspectMut.isPending ? "Reading…" : "Map columns"}
                    </Button>
                    {Object.keys(columnMap).length > 0 && (
                      <Chip size="small" label={`${Object.values(columnMap).filter(Boolean).length} mapped`} />
                    )}
                    <Button
                      variant="contained"
                      disabled={(!csvText.trim() && !xlsxFile) || previewMut.isPending || uploadPreviewMut.isPending}
                      onClick={runPreview}
                      startIcon={(previewMut.isPending || uploadPreviewMut.isPending) ? <CircularProgress size={14} /> : null}
                    >
                      {(previewMut.isPending || uploadPreviewMut.isPending) ? "Previewing…" : "Preview"}
                    </Button>
                    {preview && (
                      <Button
                        variant="contained"
                        color="success"
                        disabled={preview.will_create_count === 0 || commitMut.isPending || uploadCommitMut.isPending}
                        onClick={runCommit}
                        startIcon={(commitMut.isPending || uploadCommitMut.isPending) ? <CircularProgress size={14} /> : null}
                      >
                        {(commitMut.isPending || uploadCommitMut.isPending)
                          ? "Committing…"
                          : `Commit ${preview.will_create_count} row${preview.will_create_count === 1 ? "" : "s"}`}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Column-mapping dialog */}
            <Dialog open={mappingOpen} onClose={() => setMappingOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Map your columns</DialogTitle>
              <DialogContent dividers>
                {!inspect ? (
                  <div className="text-sm text-slate-500">Reading the file…</div>
                ) : inspect.columns.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    This importer takes the file as-is — no column mapping needed.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {inspect.structure_healed && (
                      <div className="text-xs rounded-md border border-indigo-200 bg-indigo-50 p-2 text-indigo-800 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">
                        <strong>Reshaped for import.</strong>{" "}
                        {inspect.reply?.trim() ||
                          "This file wasn't in the expected row format, so it was restructured automatically. The columns below reflect the reshaped data."}
                      </div>
                    )}
                    {!!inspect.data_notes?.length && (
                      <ul className="text-xs rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-900 space-y-1 list-disc pl-5 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                        {inspect.data_notes.map((note, i) => (
                          <li key={`${i}-${note.slice(0, 40)}`}>{note}</li>
                        ))}
                      </ul>
                    )}
                    <p className="text-xs text-slate-500">
                      Match each Ledger Core field to a column in your file. Required fields are
                      marked <span className="text-red-600">*</span>. Anything left unmapped still
                      falls back to automatic name-matching.
                    </p>
                    {inspect.columns.map((col) => (
                      <div key={col.name} className="flex items-start gap-3">
                        <div className="w-44 shrink-0 pt-2 text-sm">
                          {col.name}
                          {col.required && <span className="text-red-600"> *</span>}
                          {col.help && (
                            <div className="text-[11px] leading-tight text-slate-400">{col.help}</div>
                          )}
                        </div>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={columnMap[col.name] ?? ""}
                          onChange={(e) =>
                            setColumnMap((m) => ({ ...m, [col.name]: e.target.value }))
                          }
                        >
                          <MenuItem value="">
                            <em>— not mapped —</em>
                          </MenuItem>
                          {inspect.source_headers.map((h) => (
                            <MenuItem key={h} value={h}>{h}</MenuItem>
                          ))}
                        </TextField>
                      </div>
                    ))}
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setMappingOpen(false)}>Done</Button>
              </DialogActions>
            </Dialog>

            {/* Direct import result card */}
            {directResult && (
              <div className={
                "border rounded-md p-4 text-sm " +
                (directResult.imported
                  ? "bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-200"
                  : "bg-amber-50 border-amber-300 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200")
              }>
                <div className="font-semibold mb-1">
                  {directResult.imported ? "✓ " : "⚠ "}{directResult.summary}
                </div>
                {directResult.detail && (
                  <div className="text-xs mt-1 opacity-80">{directResult.detail}</div>
                )}
                {directResult.errors.length > 0 && (
                  <ul className="text-xs mt-2 space-y-0.5 list-disc list-inside opacity-80">
                    {directResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                )}
                {directResult.link && (
                  <a
                    href={directResult.link}
                    className="inline-block mt-2 text-xs underline"
                  >
                    View imported data →
                  </a>
                )}
              </div>
            )}

            {(preview || result) && !isDirectImport && (
              <ResultBlock title={result ? "Result" : "Preview"} data={result ?? preview!} />
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Import a past job — CONSTRUCTION-PROJECT-HISTORY-IMPORT-001
// Upload a finished job's budget → an ARCHIVED historical project that
// seeds cost history (so the Budget Agent is smart about your numbers).
// ──────────────────────────────────────────────────────────────
type HistoryPreview = {
  name: string; number: string; customer: string | null; description: string | null;
  start_date: string | null; end_date: string | null; contract_value: string | null;
  kinds: string[]; line_count: number; bid_total: string | null; final_total: string | null;
  errors: string[]; warnings: string[]; collision: boolean; import_source: string;
};
type HistoryCommit = {
  project_id: string; code: string; name: string; status: string;
  line_count: number; kinds: string[];
};

function ImportPastJobCard() {
  const toast = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<HistoryPreview | null>(null);
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<HistoryCommit | null>(null);

  const reset = () => {
    setFile(null); setPreview(null); setName(""); setNumber(""); setDone(null);
  };

  const onFile = async (f: File) => {
    setFile(f); setPreview(null); setDone(null); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const p = await api<HistoryPreview>(
        "/api/imports/project-history/preview", { method: "POST", body: fd });
      setPreview(p); setName(p.name); setNumber(p.number);
    } catch (err) {
      toast.error(`Couldn't read the file: ${(err as Error).message}`);
      setFile(null);
    } finally { setBusy(false); }
  };

  const onCommit = async () => {
    if (!file || !preview) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Always send the shown name/number so the committed project matches the
      // preview exactly. (Without this, a file with no project number would get
      // a fresh random HIST-code on commit — different from the one previewed.)
      if (name.trim()) fd.append("name", name.trim());
      if (number.trim()) fd.append("number", number.trim());
      const r = await api<HistoryCommit>(
        "/api/imports/project-history/commit", { method: "POST", body: fd });
      setDone(r);
      toast.success(`Imported ${r.name} (${r.code}) — ${r.line_count} budget lines`);
    } catch (err) {
      toast.error(`Import failed: ${(err as Error).message}`);
    } finally { setBusy(false); }
  };

  // If the number is unchanged from the extracted one, we know the collision
  // state; if edited, the backend re-checks and blocks on commit.
  const knownCollision = !!preview && number.trim() === preview.number && preview.collision;

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 dark:bg-gray-900 dark:border-gray-800">
      <div className="flex items-baseline gap-2 mb-1">
        <div className="text-sm font-medium text-slate-800 dark:text-slate-200">
          Import a past job
        </div>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          seeds cost history — the Budget Agent gets smart about your numbers
        </span>
      </div>
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-3">
        Upload a finished job's budget (CSV or Excel). We extract the project name
        + number, create an archived history record, and load the budget so it
        feeds your bid-vs-actual history. Nothing is written until you confirm.
      </div>

      {done ? (
        <div className="border rounded-md p-3 text-sm bg-emerald-50 border-emerald-300 text-emerald-900 dark:bg-emerald-900/20 dark:border-emerald-700 dark:text-emerald-200">
          <div className="font-semibold mb-1">
            ✓ Imported {done.name} ({done.code})
          </div>
          <div className="text-xs opacity-80">
            {done.line_count} budget lines · saved {done.kinds.join(" + ") || "budget"} ·
            archived (seeds cost history)
          </div>
          <div className="mt-2 flex gap-3">
            <a href="/projects" className="text-xs underline">View in Projects (filter: Archived) →</a>
            <button className="text-xs underline" onClick={reset}>Import another</button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <Button variant="outlined" size="small" component="label" disabled={busy}>
              {file ? "Choose a different file" : "Choose budget file"}
              <input
                type="file" hidden accept=".xlsx,.xlsm,.csv,.tsv,.txt"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
              />
            </Button>
            {file && <span className="text-xs text-slate-500 dark:text-slate-400">{file.name}</span>}
            {busy && <CircularProgress size={16} />}
          </div>

          {preview && (
            <div className="space-y-3 border-t border-slate-100 dark:border-gray-800 pt-3">
              <div className="flex flex-wrap gap-3">
                <TextField
                  size="small" label="Project name" value={name}
                  onChange={(e) => setName(e.target.value)} sx={{ minWidth: 240 }}
                />
                <TextField
                  size="small" label="Project number" value={number}
                  onChange={(e) => setNumber(e.target.value)} sx={{ minWidth: 180 }}
                  error={knownCollision}
                  helperText={knownCollision ? "Number already exists — rename it" : undefined}
                />
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <Chip size="small" label={`${preview.line_count} lines`} />
                {preview.kinds.map((k) => (
                  <Chip key={k} size="small" color="primary" variant="outlined"
                    label={k === "as_built" ? "as-built" : k} />
                ))}
                {preview.final_total && (
                  <Chip size="small" variant="outlined" label={`final $${preview.final_total}`} />
                )}
                {preview.bid_total && (
                  <Chip size="small" variant="outlined" label={`bid $${preview.bid_total}`} />
                )}
              </div>

              {preview.warnings.length > 0 && (
                <ul className="text-xs list-disc list-inside text-amber-700 dark:text-amber-300 space-y-0.5">
                  {preview.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
              {preview.errors.length > 0 && (
                <ul className="text-xs list-disc list-inside text-red-600 dark:text-red-400 space-y-0.5">
                  {preview.errors.slice(0, 8).map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              )}

              <div className="flex items-center gap-2">
                <Button
                  variant="contained" size="small" onClick={onCommit}
                  disabled={busy || knownCollision || preview.line_count === 0}
                >
                  Create archived project
                </Button>
                <Button size="small" onClick={reset} disabled={busy}>Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Per-kind context editor — uses bank-account picker for
// `bank_account_id`, plain text input otherwise.
// ──────────────────────────────────────────────────────────────
function ContextEditor({
  kind, contextKeys, context, onChange,
}: {
  kind: string;
  contextKeys: Record<string, string>;
  context: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const banksQuery = useQuery({
    ...bankAccountsListQueryOptions(),
    enabled: "bank_account_id" in contextKeys,
  });

  const setKey = (k: string, v: unknown) =>
    onChange({ ...context, [k]: v });

  return (
    <div className="grid grid-cols-2 gap-3">
      {Object.entries(contextKeys).map(([key, desc]) => {
        // Bank picker
        if (key === "bank_account_id") {
          return (
            <TextField
              key={key}
              select
              size="small"
              label="Bank account"
              value={(context[key] as string) ?? ""}
              onChange={(e) => setKey(key, e.target.value)}
              helperText={desc}
              sx={{ gridColumn: "span 2" }}
            >
              {(banksQuery.data ?? []).map((b) => (
                <MenuItem key={b.id} value={b.id}>
                  {b.name} ({b.bank_name}) — {b.currency_code}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        // Boolean toggles (auto_post)
        if (typeof context[key] === "boolean" || key === "auto_post") {
          return (
            <TextField
              key={key}
              select
              size="small"
              label={key}
              value={
                context[key] === undefined
                  ? "true"
                  : String(context[key])
              }
              onChange={(e) =>
                setKey(key, e.target.value === "true")
              }
              helperText={desc}
            >
              <MenuItem value="true">true</MenuItem>
              <MenuItem value="false">false</MenuItem>
            </TextField>
          );
        }
        // Generic text
        return (
          <TextField
            key={key}
            size="small"
            label={key}
            value={(context[key] as string) ?? ""}
            onChange={(e) => setKey(key, e.target.value || undefined)}
            helperText={desc}
          />
        );
      })}
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Preview / Result renderer
// ──────────────────────────────────────────────────────────────
function ResultBlock({
  title, data,
}: {
  title: string;
  data: ImportPreview | ImportResult;
}) {
  const isResult = "created_count" in data;
  const created = isResult
    ? (data as ImportResult).created_count
    : (data as ImportPreview).will_create_count;

  const grouped = useMemo(() => {
    const out = { ok: 0, warning: 0, duplicate: 0, error: 0 } as Record<RowStatus, number>;
    for (const r of data.rows) out[r.status]++;
    return out;
  }, [data]);

  return (
    <div className="bg-white border border-slate-200 rounded-md dark:bg-gray-900 dark:border-gray-800">
      <div className="px-4 py-3 border-b border-slate-200 flex items-baseline gap-3 dark:border-gray-800">
        <span className="text-sm font-medium">{title}</span>
        <Chip
          size="small"
          label={`${data.total_rows} rows`}
          sx={{ fontSize: 10, height: 18 }}
        />
        {(["ok", "warning", "duplicate", "error"] as RowStatus[]).map((s) =>
          grouped[s] ? (
            <Chip
              key={s}
              size="small"
              label={`${grouped[s]} ${s}`}
              sx={{
                fontSize: 10, height: 18,
                bgcolor: STATUS_BG[s], color: STATUS_COLOR[s],
                fontWeight: 600,
              }}
            />
          ) : null,
        )}
        <Box sx={{ flex: 1 }} />
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {isResult
            ? `${created} created`
            : `${created} would be created`}
        </span>
      </div>

      {/* Summary banner */}
      {Object.keys(data.summary).some((k) => !CHAT_ONLY_SUMMARY_KEYS.has(k)) && (
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs text-slate-700 font-mono dark:bg-gray-950 dark:border-gray-800 dark:text-slate-300">
          {Object.entries(data.summary)
            // Keys the chat uses to load and read a file — not for this banner.
            .filter(([k]) => !CHAT_ONLY_SUMMARY_KEYS.has(k))
            .map(([k, v]) => (
            <span key={k} className="mr-4">
              <span className="text-slate-500 dark:text-slate-400">{k}:</span> {String(v)}
            </span>
          ))}
        </div>
      )}

      {/* Per-row table */}
      <table className="w-full text-xs">
        <thead className="bg-white border-b border-slate-200 text-[10px] uppercase tracking-wide text-slate-600 dark:bg-gray-900 dark:border-gray-800 dark:text-slate-300">
          <tr>
            <th className="text-left px-4 py-2 w-12">#</th>
            <th className="text-left px-3 py-2 w-20">Status</th>
            <th className="text-left px-3 py-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <RowRender key={r.row_no} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function RowRender({ row }: { row: RowOutcome }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <tr
        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer dark:border-gray-800 dark:hover:bg-gray-800"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-1.5 font-mono text-slate-500 dark:text-slate-400">{row.row_no}</td>
        <td className="px-3 py-1.5">
          <Chip
            size="small"
            label={row.status}
            sx={{
              fontSize: 10, height: 18,
              bgcolor: STATUS_BG[row.status],
              color: STATUS_COLOR[row.status],
              fontWeight: 600,
            }}
          />
        </td>
        <td className="px-3 py-1.5">
          {row.message && (
            <div className="text-slate-700 dark:text-slate-300">{row.message}</div>
          )}
          {row.will_create && (
            <div className="text-slate-500 mt-0.5 font-mono text-[11px] dark:text-slate-400">
              {compactPreview(row.will_create)}
            </div>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-t border-slate-100 bg-slate-50 dark:bg-gray-950 dark:border-gray-800">
          <td colSpan={3} className="px-12 py-2">
            <div className="grid grid-cols-2 gap-4">
              <pre className="text-[10px] whitespace-pre-wrap break-words font-mono bg-white p-2 rounded border border-slate-200 dark:bg-gray-900 dark:border-gray-800">
                <strong>source_data:</strong>{"\n"}
                {JSON.stringify(row.source_data, null, 2)}
              </pre>
              {row.will_create && (
                <pre className="text-[10px] whitespace-pre-wrap break-words font-mono bg-white p-2 rounded border border-slate-200 dark:bg-gray-900 dark:border-gray-800">
                  <strong>will_create:</strong>{"\n"}
                  {JSON.stringify(row.will_create, null, 2)}
                </pre>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}


function compactPreview(obj: Record<string, unknown>): string {
  // Show 3-4 most-relevant fields as a short summary; click row to expand
  const keys = Object.keys(obj).slice(0, 4);
  return keys
    .map((k) => `${k}=${JSON.stringify(obj[k])}`)
    .join("  ");
}
